/**
 * components/ThemeToggle.tsx
 * ------------------------------------------------------------------
 * Three-way light / system / dark selector for the Shell header.
 * Desktop: segmented control. Mobile: single button that cycles.
 * State owned by useTheme() (lib/theme.ts).
 * ------------------------------------------------------------------ */
import { Sun, Monitor, Moon } from 'lucide-react';
import { useTheme, type ThemeMode } from '@/lib/theme';

interface Option {
  mode: ThemeMode;
  Icon: typeof Sun;
  label: string;
}

const OPTIONS: Option[] = [
  { mode: 'light', Icon: Sun, label: 'Light' },
  { mode: 'system', Icon: Monitor, label: 'System' },
  { mode: 'dark', Icon: Moon, label: 'Dark' },
];

export default function ThemeToggle() {
  const { mode, setMode, effective } = useTheme();

  function cycle() {
    const order: ThemeMode[] = ['light', 'system', 'dark'];
    const idx = order.indexOf(mode);
    const next = order[(idx + 1) % order.length]!;
    setMode(next);
  }

  const ActiveIcon = OPTIONS.find((o) => o.mode === mode)?.Icon ?? Monitor;

  return (
    <>
      {/* Desktop / tablet: segmented control */}
      <div
        role="radiogroup"
        aria-label="Theme"
        className="hidden items-center rounded-xl border border-black/10 bg-white p-0.5 dark:border-white/10 dark:bg-white/5 md:inline-flex"
      >
        {OPTIONS.map((opt) => {
          const Icon = opt.Icon;
          const active = mode === opt.mode;
          return (
            <button
              key={opt.mode}
              role="radio"
              aria-checked={active}
              aria-label={opt.label}
              title={opt.label}
              onClick={() => setMode(opt.mode)}
              className={`rounded-lg p-1.5 transition-colors ${
                active
                  ? 'bg-brand text-white'
                  : 'text-gray-500 hover:text-gray-800 dark:hover:text-gray-200'
              }`}
            >
              <Icon size={14} strokeWidth={2} />
            </button>
          );
        })}
      </div>

      {/* Mobile: single icon button that cycles */}
      <button
        onClick={cycle}
        aria-label={`Theme: ${mode} (currently ${effective}). Tap to change.`}
        title={`Theme: ${mode}`}
        className="rounded-xl border border-black/10 bg-white p-2 text-gray-600 hover:text-gray-800 dark:border-white/10 dark:bg-white/5 dark:text-gray-300 dark:hover:text-gray-100 md:hidden"
      >
        <ActiveIcon size={16} strokeWidth={2} />
      </button>
    </>
  );
}
